import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Mail,
  Save,
  RotateCcw,
  TestTube,
  AlertTriangle,
  CheckCircle,
  Loader,
  Server,
  Send,
  Download,
  Building,
  Eye,
  EyeOff,
  Play,
  Square,
  Settings,
  Clock
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';
import { api } from '../../../utils/api';

const EmailConfig = () => {
  const { user, hasRole } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  // Check if user has access (Admin or Manager)
  const hasAccess = hasRole(['Admin', 'Manager']);
  
  // Get current tenant info safely
  const getCurrentTenant = () => {
    try {
      return getProtectedTenantInfo();
    } catch (error) {
      console.error('❌ Failed to get tenant info:', error);
      throw new Error('Invalid tenant context detected');
    }
  };
  
  const [emailConfig, setEmailConfig] = useState({
    smtp: {
      enabled: false,
      host: '',
      port: 587,
      secure: false,
      username: '',
      password: '',
      fromName: 'CRM System',
      fromAddress: 'noreply@company.com',
      showPassword: false
    },
    imap: {
      enabled: false,
      host: '',
      port: 993,
      secure: true,
      username: '',
      password: '',
      showPassword: false
    },
    processing: {
      enabled: false,
      checkInterval: 300000, // 5 minutes in milliseconds
      maxEmailsPerCheck: 50,
      deleteAfterProcessing: false,
      automaticStart: false,
      autoCreateSalesNotes: true,
      autoCreateKnowledge: true,
      ccEmailPatterns: ['cc@', 'carboncopy@', 'copy@'],
      directEmailPatterns: ['direct@', 'info@', 'contact@', 'support@']
    }
  });

  const [status, setStatus] = useState({
    jobRunning: false,
    lastChecked: null,
    lastError: null,
    emailsProcessed: 0,
    totalEmails: 0
  });



  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ smtp: false, imap: false });
  const [jobControl, setJobControl] = useState({ starting: false, stopping: false });
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  useEffect(() => {
    if (hasAccess) {
      Promise.all([
        loadEmailConfiguration(),
        loadEmailStatus()
      ]);
    }
  }, [hasAccess]);

  // Polling effect for status updates when job is running
  useEffect(() => {
    let intervalId;
    
    if (status?.jobRunning) {
      intervalId = setInterval(async () => {
        await loadEmailStatus();
      }, 15000); // Poll every 15 seconds (reduced from 5 seconds to reduce server load)
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status?.jobRunning]);

  const loadEmailConfiguration = async () => {
    try {
      setLoading(true);
      const tenant = getCurrentTenant();
      
      console.log('🔍 Loading email config for tenant:', tenant);
      console.log('🔍 Making API call to:', `/email-config/tenant/${tenant.code}`);
      
      const configData = await api.get(`/email-config/tenant/${tenant.code}`);
      
      console.log('✅ Received email config data:', configData);
      
      if (configData && typeof configData === 'object' && (configData.smtp || configData.imap || configData.processing)) {
        console.log('✅ Setting email config:', configData);
        setEmailConfig(configData);
      } else {
        console.warn('⚠️ No valid email configuration data found, using defaults');
        // Set default config if none found
        setEmailConfig({
          smtp: {
            enabled: false,
            host: '',
            port: 587,
            secure: false,
            username: '',
            password: '',
            fromName: 'CRM System',
            fromAddress: 'noreply@company.com',
            showPassword: false
          },
          imap: {
            enabled: false,
            host: '',
            port: 993,
            secure: true,
            username: '',
            password: '',
            showPassword: false
          },
          processing: {
            enabled: false,
            checkInterval: 300000,
            maxEmailsPerCheck: 50,
            deleteAfterProcessing: false,
            automaticStart: false,
            autoCreateSalesNotes: true,
            autoCreateKnowledge: true,
            ccEmailPatterns: ['cc@', 'carboncopy@', 'copy@'],
            directEmailPatterns: ['direct@', 'info@', 'contact@', 'support@']
          }
        });
      }
    } catch (error) {
      console.error('❌ Failed to load email configuration:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      
      // Set default config on error and show toast
      setEmailConfig({
        smtp: {
          enabled: false,
          host: '',
          port: 587,
          secure: false,
          username: '',
          password: '',
          fromName: 'CRM System',
          fromAddress: 'noreply@company.com',
          showPassword: false
        },
        imap: {
          enabled: false,
          host: '',
          port: 993,
          secure: true,
          username: '',
          password: '',
          showPassword: false
        },
        processing: {
          enabled: false,
          checkInterval: 300000,
          maxEmailsPerCheck: 50,
          deleteAfterProcessing: false,
          automaticStart: false,
          autoCreateSalesNotes: true,
          autoCreateKnowledge: true,
          ccEmailPatterns: ['cc@', 'carboncopy@', 'copy@'],
          directEmailPatterns: ['direct@', 'info@', 'contact@', 'support@']
        }
      });
      
      toast.error('Failed to load email configuration: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const saveEmailConfiguration = async () => {
    try {
      setSaving(true);
      const tenant = getCurrentTenant();
      
      const response = await api.put(`/email-config/tenant/${tenant.code}`, emailConfig);
      toast.success('Email configuration saved successfully');
      await loadEmailStatus();
    } catch (error) {
      console.error('Failed to save email configuration:', error);
      toast.error('Failed to save email configuration: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const loadEmailStatus = async () => {
    try {
      setRefreshingStatus(true);
      const tenant = getCurrentTenant();
      
      console.log('🔍 Loading email status for tenant:', tenant.code);
      
      const response = await api.get(`/email-config/status/${tenant.code}`);
      
      console.log('✅ Received email status:', response);
      
      const statusData = response || {
        jobRunning: false,
        lastChecked: null,
        lastError: null,
        emailsProcessed: 0,
        totalEmails: 0
      };
      
      setStatus(statusData);
    } catch (error) {
      console.error('❌ Failed to load email status:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      // Set default status on error (don't show toast for status failures)
      setStatus({
        jobRunning: false,
        lastChecked: null,
        lastError: 'Failed to load status',
        emailsProcessed: 0,
        totalEmails: 0
      });
    } finally {
      setRefreshingStatus(false);
    }
  };

  const updateSmtpConfig = (field, value) => {
    // Trim whitespace from host field
    if (field === 'host' && typeof value === 'string') {
      value = value.trim();
    }
    
    setEmailConfig(prev => ({
      ...prev,
      smtp: {
        ...prev.smtp,
        [field]: value
      }
    }));
  };

  const updateImapConfig = (field, value) => {
    // Trim whitespace from host field
    if (field === 'host' && typeof value === 'string') {
      value = value.trim();
    }
    
    setEmailConfig(prev => ({
      ...prev,
      imap: {
        ...prev.imap,
        [field]: value
      }
    }));
  };

  const updateProcessingConfig = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      processing: {
        ...prev.processing,
        [field]: value
      }
    }));
  };

  const updateEmailPattern = (type, index, value) => {
    setEmailConfig(prev => ({
      ...prev,
      processing: {
        ...prev.processing,
        [type]: prev.processing[type].map((pattern, i) => 
          i === index ? value : pattern
        )
      }
    }));
  };

  const addEmailPattern = (type) => {
    setEmailConfig(prev => ({
      ...prev,
      processing: {
        ...prev.processing,
        [type]: [...prev.processing[type], '']
      }
    }));
  };

  const removeEmailPattern = (type, index) => {
    setEmailConfig(prev => ({
      ...prev,
      processing: {
        ...prev.processing,
        [type]: prev.processing[type].filter((_, i) => i !== index)
      }
    }));
  };

  const testSmtpConnection = async () => {
    try {
      setTesting(prev => ({ ...prev, smtp: true }));
      const tenant = getCurrentTenant();
      await api.post(`/email-config/test-smtp/${tenant.code}`, emailConfig.smtp);
      toast.success('SMTP connection test successful');
    } catch (error) {
      console.error('SMTP test failed:', error);
      toast.error('SMTP connection test failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setTesting(prev => ({ ...prev, smtp: false }));
    }
  };

  const testImapConnection = async () => {
    try {
      setTesting(prev => ({ ...prev, imap: true }));
      const tenant = getCurrentTenant();
      await api.post(`/email-config/test-imap/${tenant.code}`, emailConfig.imap);
      toast.success('IMAP connection test successful');
    } catch (error) {
      console.error('IMAP test failed:', error);
      toast.error('IMAP connection test failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setTesting(prev => ({ ...prev, imap: false }));
    }
  };

  const startEmailJob = async () => {
    try {
      setJobControl(prev => ({ ...prev, starting: true }));
      const tenant = getCurrentTenant();
      
      // Check if processing is enabled
      if (!emailConfig.processing.enabled) {
        toast.error('Email processing is not enabled. Please enable it in the Email Processing Settings section.');
        return;
      }
      
      // Check if IMAP is configured
      if (!emailConfig.imap.enabled || !emailConfig.imap.host || !emailConfig.imap.username) {
        toast.error('IMAP configuration is incomplete. Please configure IMAP settings first.');
        return;
      }
      
      await api.post(`/email-config/start-job/${tenant.code}`);
      toast.success('Email processing job started successfully');
      
      // Add a small delay to allow the backend to update the status
      setTimeout(async () => {
        await loadEmailStatus();
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start email job:', error);
      toast.error('Failed to start email job: ' + (error.response?.data?.error || error.message));
    } finally {
      setJobControl(prev => ({ ...prev, starting: false }));
    }
  };

  const stopEmailJob = async () => {
    try {
      setJobControl(prev => ({ ...prev, stopping: true }));
      const tenant = getCurrentTenant();
      
      await api.post(`/email-config/stop-job/${tenant.code}`);
      toast.success('Email processing job stopped successfully');
      
      // Add a small delay to allow the backend to update the status
      setTimeout(async () => {
        await loadEmailStatus();
      }, 1000);
      
    } catch (error) {
      console.error('Failed to stop email job:', error);
      toast.error('Failed to stop email job: ' + (error.response?.data?.error || error.message));
    } finally {
      setJobControl(prev => ({ ...prev, stopping: false }));
    }
  };

  const checkEmailsNow = async () => {
    try {
      const tenant = getCurrentTenant();
      await api.post(`/email-config/check-now/${tenant.code}`);
      toast.success('Email check initiated successfully');
      await loadEmailStatus();
    } catch (error) {
      console.error('Failed to check emails:', error);
      toast.error('Failed to check emails: ' + (error.response?.data?.error || error.message));
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Loading email configuration...</p>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Access Denied</h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
            You need Admin or Manager permissions to access Email Configuration.
          </p>
        </div>
      </div>
    );
  }

  const currentTenant = getCurrentTenant();

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className={`flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          <Building className="h-4 w-4" />
          <span className="text-sm">Configure email settings for {currentTenant.name} ({currentTenant.code})</span>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => window.location.reload()}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={saveEmailConfiguration}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>

      {/* Email Service Status */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <h2 className={`text-lg font-medium mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Service Status</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${status?.jobRunning ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {status?.jobRunning ? 'Running' : 'Stopped'}
              </span>
              {status?.jobRunning && (
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              )}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-medium">Last Check:</span>
              <span className="ml-2">
                {status?.lastChecked ? new Date(status.lastChecked).toLocaleString() : 'Never'}
              </span>
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-medium">Processed:</span>
              <span className="ml-2">{status?.emailsProcessed || 0}</span>
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-medium">Total:</span>
              <span className="ml-2">{status?.totalEmails || 0}</span>
            </div>
          </div>

          {status?.lastError && (
            <div className={`mb-6 p-3 rounded-lg ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>Last Error:</span>
              </div>
              <p className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-700'}`}>{status.lastError}</p>
            </div>
          )}

          {!emailConfig.processing.enabled && (
            <div className={`mb-6 p-3 rounded-lg ${isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className={`text-sm font-medium ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>Configuration Required:</span>
              </div>
              <p className={`text-sm mt-1 ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                Email processing is disabled. Enable it in the Email Processing Settings section below to start the job.
              </p>
            </div>
          )}

          
          
          <div className="flex space-x-3">
            <button
              onClick={startEmailJob}
              disabled={!emailConfig.processing.enabled || status?.jobRunning || jobControl.starting}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!emailConfig.processing.enabled ? 'Enable Email Processing first' : ''}
            >
              {jobControl.starting ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              <span>{jobControl.starting ? 'Starting...' : 'Start Job'}</span>
            </button>
            <button
              onClick={stopEmailJob}
              disabled={!status?.jobRunning || jobControl.stopping}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {jobControl.stopping ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              <span>{jobControl.stopping ? 'Stopping...' : 'Stop Job'}</span>
            </button>
            <button
              onClick={checkEmailsNow}
              disabled={!emailConfig.processing.enabled}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              <span>Check Emails Now</span>
            </button>
            <button
              onClick={loadEmailStatus}
              disabled={refreshingStatus}
              className={`flex items-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                isDark 
                  ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              {refreshingStatus ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              <span>{refreshingStatus ? 'Refreshing...' : 'Refresh Status'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-100'}`}>
                <Send className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>SMTP Configuration</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Configure tenant outgoing email settings</p>
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={emailConfig.smtp.enabled}
                onChange={(e) => updateSmtpConfig('enabled', e.target.checked)}
                className={`rounded text-blue-600 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                SMTP Host
              </label>
              <input
                type="text"
                value={emailConfig.smtp.host}
                onChange={(e) => updateSmtpConfig('host', e.target.value)}
                disabled={!emailConfig.smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Port
              </label>
              <input
                type="number"
                value={emailConfig.smtp.port}
                onChange={(e) => updateSmtpConfig('port', parseInt(e.target.value))}
                disabled={!emailConfig.smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Username
              </label>
              <input
                type="text"
                value={emailConfig.smtp.username}
                onChange={(e) => updateSmtpConfig('username', e.target.value)}
                disabled={!emailConfig.smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="tenant-email@company.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Password
              </label>
              <div className="relative">
                <input
                  type={emailConfig.smtp.showPassword ? 'text' : 'password'}
                  value={emailConfig.smtp.password}
                  onChange={(e) => updateSmtpConfig('password', e.target.value)}
                  disabled={!emailConfig.smtp.enabled}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                      : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                  }`}
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {emailConfig.smtp.showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateSmtpConfig('showPassword', false)} />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateSmtpConfig('showPassword', true)} />
                  )}
                </span>
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                From Name
              </label>
              <input
                type="text"
                value={emailConfig.smtp.fromName}
                onChange={(e) => updateSmtpConfig('fromName', e.target.value)}
                disabled={!emailConfig.smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                From Address
              </label>
              <input
                type="email"
                value={emailConfig.smtp.fromAddress}
                onChange={(e) => updateSmtpConfig('fromAddress', e.target.value)}
                disabled={!emailConfig.smtp.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={emailConfig.smtp.secure}
                onChange={(e) => updateSmtpConfig('secure', e.target.checked)}
                disabled={!emailConfig.smtp.enabled}
                className={`mr-2 rounded text-blue-600 focus:ring-blue-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Use SSL/TLS</label>
            </div>
            <div className="flex items-end">
              <button
                onClick={testSmtpConnection}
                disabled={testing.smtp || !emailConfig.smtp.enabled || !emailConfig.smtp.host || !emailConfig.smtp.username}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                {testing.smtp ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                <span>{testing.smtp ? 'Testing...' : 'Test Connection'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* IMAP Configuration */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-100'}`}>
                <Download className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>IMAP Configuration</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Configure tenant incoming email settings</p>
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={emailConfig.imap.enabled}
                onChange={(e) => updateImapConfig('enabled', e.target.checked)}
                className={`rounded text-green-600 focus:ring-green-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                IMAP Host
              </label>
              <input
                type="text"
                value={emailConfig.imap.host}
                onChange={(e) => updateImapConfig('host', e.target.value)}
                disabled={!emailConfig.imap.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="imap.gmail.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Port
              </label>
              <input
                type="number"
                value={emailConfig.imap.port}
                onChange={(e) => updateImapConfig('port', parseInt(e.target.value))}
                disabled={!emailConfig.imap.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Username
              </label>
              <input
                type="text"
                value={emailConfig.imap.username}
                onChange={(e) => updateImapConfig('username', e.target.value)}
                disabled={!emailConfig.imap.enabled}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
                placeholder="tenant-email@company.com"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Password
              </label>
              <div className="relative">
                <input
                  type={emailConfig.imap.showPassword ? 'text' : 'password'}
                  value={emailConfig.imap.password}
                  onChange={(e) => updateImapConfig('password', e.target.value)}
                  disabled={!emailConfig.imap.enabled}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                      : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                  }`}
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {emailConfig.imap.showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateImapConfig('showPassword', false)} />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => updateImapConfig('showPassword', true)} />
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={emailConfig.imap.secure}
                onChange={(e) => updateImapConfig('secure', e.target.checked)}
                disabled={!emailConfig.imap.enabled}
                className={`mr-2 rounded text-green-600 focus:ring-green-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Use SSL/TLS</label>
            </div>
            <div className="flex items-end">
              <button
                onClick={testImapConnection}
                disabled={testing.imap || !emailConfig.imap.enabled || !emailConfig.imap.host || !emailConfig.imap.username}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                {testing.imap ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                <span>{testing.imap ? 'Testing...' : 'Test Connection'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Processing Settings */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-100'}`}>
                <Settings className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Processing Settings</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Configure tenant email processing and automation</p>
              </div>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={emailConfig.processing.enabled}
                onChange={(e) => updateProcessingConfig('enabled', e.target.checked)}
                className={`rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enabled</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Check Interval (minutes)
              </label>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  value={Math.floor(emailConfig.processing.checkInterval / 60000)}
                  onChange={(e) => updateProcessingConfig('checkInterval', parseInt(e.target.value) * 60000)}
                  disabled={!emailConfig.processing.enabled}
                  min="1"
                  max="60"
                  className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                      : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                  }`}
                />
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Max Emails Per Check
              </label>
              <input
                type="number"
                value={emailConfig.processing.maxEmailsPerCheck}
                onChange={(e) => updateProcessingConfig('maxEmailsPerCheck', parseInt(e.target.value))}
                disabled={!emailConfig.processing.enabled}
                min="1"
                max="100"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                    : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                }`}
              />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={emailConfig.processing.autoCreateSalesNotes}
                onChange={(e) => updateProcessingConfig('autoCreateSalesNotes', e.target.checked)}
                disabled={!emailConfig.processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Auto-create Notes from CC emails
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={emailConfig.processing.autoCreateKnowledge}
                onChange={(e) => updateProcessingConfig('autoCreateKnowledge', e.target.checked)}
                disabled={!emailConfig.processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Auto-create Knowledge Articles from direct emails
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={emailConfig.processing.deleteAfterProcessing}
                onChange={(e) => updateProcessingConfig('deleteAfterProcessing', e.target.checked)}
                disabled={!emailConfig.processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Delete emails after processing
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={emailConfig.processing.automaticStart || false}
                onChange={(e) => updateProcessingConfig('automaticStart', e.target.checked)}
                disabled={!emailConfig.processing.enabled}
                className={`mr-3 rounded text-purple-600 focus:ring-purple-500 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
              <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Auto start email checking process
              </label>
            </div>
          </div>

          {/* Email Classification Patterns */}
          <div className="space-y-6">
            <div>
              <h4 className={`text-md font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                CC Email Patterns (Creates Notes)
              </h4>
              <div className="space-y-2">
                {emailConfig.processing.ccEmailPatterns.map((pattern, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={pattern}
                      onChange={(e) => updateEmailPattern('ccEmailPatterns', index, e.target.value)}
                      disabled={!emailConfig.processing.enabled}
                      className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                          : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                      }`}
                      placeholder="cc@"
                    />
                    <button
                      onClick={() => removeEmailPattern('ccEmailPatterns', index)}
                      disabled={!emailConfig.processing.enabled}
                      className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addEmailPattern('ccEmailPatterns')}
                  disabled={!emailConfig.processing.enabled}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Add Pattern
                </button>
              </div>
            </div>
            
            <div>
              <h4 className={`text-md font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                Direct Email Patterns (Creates Knowledge Articles)
              </h4>
              <div className="space-y-2">
                {emailConfig.processing.directEmailPatterns.map((pattern, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={pattern}
                      onChange={(e) => updateEmailPattern('directEmailPatterns', index, e.target.value)}
                      disabled={!emailConfig.processing.enabled}
                      className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700 text-white disabled:bg-gray-800' 
                          : 'border-gray-300 bg-white text-gray-900 disabled:bg-gray-50'
                      }`}
                      placeholder="info@"
                    />
                    <button
                      onClick={() => removeEmailPattern('directEmailPatterns', index)}
                      disabled={!emailConfig.processing.enabled}
                      className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addEmailPattern('directEmailPatterns')}
                  disabled={!emailConfig.processing.enabled}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Add Pattern
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;
